import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, MapPin, Briefcase, Clock, Users, Building,
  Calendar, CheckCircle, AlertCircle, FileText, User, Mic
} from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import jobService, { JobDetail } from '../services/jobService';
import { useAuth } from '../contexts/AuthContext';
import { FloatingParticles } from '../components/animations/FloatingParticles';
import ApplicationStatusModal from '../components/modals/ApplicationStatusModal';

// Custom markdown components for consistent styling
const markdownComponents: Partial<Components> = {
  h1: ({ children }) => (
    <h3 className="text-xl font-bold text-white pb-3 border-b border-white/10 mb-4">{children}</h3>
  ),
  h2: ({ children }) => (
    <h3 className="text-xl font-bold text-white pb-3 border-b border-white/10 mb-4">{children}</h3>
  ),
  h3: ({ children }) => (
    <h4 className="text-lg font-semibold text-amber-100 mb-3">{children}</h4>
  ),
  h4: ({ children }) => (
    <h5 className="text-base font-medium text-amber-200 mb-2">{children}</h5>
  ),
  p: ({ children }) => (
    <p className="text-gray-300 leading-relaxed text-base break-words mb-3">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="space-y-2.5 mb-4 list-disc ml-4 marker:text-amber-400">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="space-y-2.5 mb-4 list-decimal ml-4 marker:text-amber-400">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-gray-300 leading-relaxed text-base break-words pl-1">{children}</li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-white">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic text-gray-200">{children}</em>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      className="text-amber-400 hover:text-amber-300 underline transition-colors duration-200"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-amber-500/50 pl-4 py-2 my-4 bg-white/5 rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children }) => (
    <code className="bg-white/10 px-1.5 py-0.5 rounded text-amber-200 text-sm">{children}</code>
  ),
  hr: () => (
    <hr className="border-white/10 my-6" />
  ),
};

// Markdown content component
const MarkdownContent: React.FC<{ content: string }> = ({ content }) => {
  if (!content) return null;

  return (
    <div className="prose prose-invert max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

const JobDetailPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [job, setJob] = useState<JobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [applicationError, setApplicationError] = useState<string | null>(null);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [startingInterview, setStartingInterview] = useState(false);
  const [showApplicationStatusModal, setShowApplicationStatusModal] = useState(false);
  const [applicationDetails, setApplicationDetails] = useState<{
    applicationStatus?: string;
    appliedDate?: string;
    interviewUrl?: string;
    interviewStatus?: 'in_progress' | 'completed' | 'not_started';
    hasApplied?: boolean;
  }>({});

  useEffect(() => {
    if (jobId) {
      loadJobDetails();
    }
  }, [jobId]);

  // Reload job details when user authentication state changes
  useEffect(() => {
    if (!authLoading && isAuthenticated && jobId) {
      loadJobDetails();
    }
  }, [authLoading, isAuthenticated, jobId]);

  // Handle user returns from login/register
  useEffect(() => {
    if (authLoading) return; // Don't proceed while auth is loading

    const shouldCheckStatus = location.state?.checkStatusOnly && isAuthenticated;

    if (shouldCheckStatus && job) {
      // User just logged in - create interview session immediately (don't apply for job)
      handleCreateInterview();
    }

    // Clear the location state to prevent repeated attempts on subsequent re-renders
    if (shouldCheckStatus && job) {
      window.history.replaceState({}, document.title);
    }
  }, [authLoading, isAuthenticated, job, location.state]);

  const handleCreateInterview = async () => {
    setStartingInterview(true);
    setApplicationError(null);

    try {
      // First check if user has already applied/interviewed for this job
      await checkApplicationStatus();
    } catch (error: any) {
      // If authentication error, let the user handle it themselves
      if (error.requiresAuth) {
        setApplicationError('Please log in to continue with your interview.');
        // Don't auto-redirect - let user click login themselves
        return;
      }

      const errorMessage = error instanceof Error ? error.message : 'Failed to process request';
      setApplicationError(errorMessage);

      // Auto-hide error after 10 seconds
      setTimeout(() => {
        setApplicationError(null);
      }, 10000);
    } finally {
      setStartingInterview(false);
    }
  };

  const checkApplicationStatus = async () => {
    const isComingFromRegistration = location.state?.checkStatusOnly;

    try {
      const status = await jobService.getApplicationStatus(jobId!);

      if (status.hasApplied) {
        // User has already applied
        setApplicationDetails({
          applicationStatus: status.applicationStatus || 'pending',
          appliedDate: status.appliedDate,
          interviewUrl: status.interviewUrl,
          interviewStatus: status.interviewStatus || 'not_started',
          hasApplied: true
        });

        if (isComingFromRegistration) {
          // Coming from registration - show interview modal instead of status modal
          setShowInterviewModal(true);
        } else {
          // Normal browsing - show status modal
          setShowApplicationStatusModal(true);
        }
      } else {
        // User hasn't applied - create new interview session
        const response = await jobService.createInterviewSession(jobId!);

        if (response.success) {
          // Show interview modal for new application
          setShowInterviewModal(true);
        }
      }
    } catch (error: any) {
      // If we can't check status (likely user hasn't applied), create new interview
      try {
        const response = await jobService.createInterviewSession(jobId!);

        if (response.success) {
          setShowInterviewModal(true);
        }
      } catch (createError: any) {
        throw createError; // Re-throw to be caught by handleCreateInterview
      }
    }
  };

  const loadJobDetails = async () => {
    setLoading(true);
    try {
      const jobData = await jobService.getJobById(jobId!);
      setJob(jobData);
    } catch (error) {
      setJob(null);
    }
    setLoading(false);
  };

  const handleApply = async () => {
    // Don't proceed if auth is still loading
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setShowAuthPrompt(true);
      return;
    }

    // For authenticated users, create interview session immediately
    await handleCreateInterview();
  };


  const formatDeadline = (dateString?: string) => {
    if (!dateString) return 'No deadline';

    const deadline = new Date(dateString);

    // Check if date is invalid
    if (isNaN(deadline.getTime())) return 'No deadline';

    const now = new Date();
    const diffTime = deadline.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // If deadline has passed
    if (diffDays < 0) return 'Closed';

    // If deadline is today
    if (diffDays === 0) return 'Ends today';
    // If deadline is tomorrow
    if (diffDays === 1) return 'Ends tomorrow';
    // If deadline within a week
    if (diffDays < 7) return `Ends in ${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
    // If deadline within a month
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Ends in ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
    }
    // Otherwise show months
    const months = Math.floor(diffDays / 30);
    return `Ends in ${months} ${months === 1 ? 'month' : 'months'}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        <FloatingParticles />
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl p-12 shadow-2xl"
        >
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500/30 to-pink-500/30 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-6 border border-purple-400/30">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
            <p className="text-white font-medium">Loading job details...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center relative overflow-hidden">
        <FloatingParticles />
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative z-10 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl p-12 shadow-2xl text-center"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-red-500/30 to-orange-500/30 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-6 border border-red-400/30">
            <AlertCircle className="w-8 h-8 text-red-300" />
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-4">Job not found</h2>
          <p className="text-gray-400 mb-6">The position you're looking for doesn't exist or has been removed.</p>
          <Link 
            to="/careers" 
            className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-semibold rounded-2xl hover:from-amber-400 hover:to-yellow-400 transition-all duration-300 shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to careers
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <FloatingParticles />
      
      {/* Job Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Smart Back Button - Subtle Apple-inspired */}
        <motion.button
          onClick={() => {
            // Smart navigation based on authentication state
            if (isAuthenticated) {
              // User is logged in, likely came from dashboard
              navigate('/dashboard');
            } else {
              // User is not logged in, likely came from careers page
              navigate('/careers');
            }
          }}
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl px-4 py-3 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 group shadow-lg mb-8 sm:mb-12"
        >
          <div className="w-8 h-8 bg-white/10 backdrop-blur-xl rounded-lg flex items-center justify-center border border-white/15 group-hover:bg-white/15 group-hover:border-white/25 transition-all duration-200">
            <ArrowLeft className="w-4 h-4 text-gray-300 group-hover:text-white transition-colors duration-200" />
          </div>
          <div className="flex flex-col items-start">
            <span className="text-white text-sm font-semibold leading-tight">
              {isAuthenticated ? 'Back to Dashboard' : 'Back to Careers'}
            </span>
            <span className="text-gray-400 text-xs leading-tight group-hover:text-gray-300 transition-colors duration-200">
              {isAuthenticated ? 'View your applications' : 'Browse all positions'}
            </span>
          </div>
        </motion.button>

      {/* Application Feedback Messages */}
      {applicationError && (
        <motion.div
          initial={{ opacity: 0, y: -50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -50, scale: 0.9 }}
          className="fixed top-6 right-6 z-50 bg-gradient-to-br from-red-500/20 to-red-600/30 backdrop-blur-2xl border border-red-500/30 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 max-w-md"
        >
          <div className="w-10 h-10 bg-red-500/30 backdrop-blur-xl rounded-full flex items-center justify-center border border-red-400/30">
            <AlertCircle className="w-5 h-5 text-red-300" />
          </div>
          <div className="flex-1">
            <p className="text-red-100 font-medium">{applicationError}</p>
          </div>
          <button 
            onClick={() => setApplicationError(null)}
            className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-all duration-200"
          >
            <span className="text-white/80 hover:text-white text-lg">×</span>
          </button>
        </motion.div>
      )}

        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-xl"
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-white via-amber-100 to-yellow-200 bg-clip-text text-transparent mb-4 sm:mb-6 leading-tight">{job.title}</h1>
              
              <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 sm:mb-8">
                {job.department && (
                  <div className="flex items-center gap-2 bg-white/8 backdrop-blur-xl rounded-full px-3 py-1.5 border border-white/15">
                    <div className="w-5 h-5 bg-gradient-to-br from-amber-500/30 to-yellow-500/30 rounded-full flex items-center justify-center">
                      <Building className="w-3 h-3 text-amber-300" />
                    </div>
                    <span className="text-gray-200 text-sm font-medium">{job.department}</span>
                  </div>
                )}
                {job.location && (
                  <div className="flex items-center gap-2 bg-white/8 backdrop-blur-xl rounded-full px-3 py-1.5 border border-white/15">
                    <div className="w-5 h-5 bg-gradient-to-br from-emerald-500/30 to-teal-500/30 rounded-full flex items-center justify-center">
                      <MapPin className="w-3 h-3 text-emerald-300" />
                    </div>
                    <span className="text-gray-200 text-sm font-medium">{job.location}</span>
                  </div>
                )}
                {job.jobType && (
                  <div className="flex items-center gap-2 bg-white/8 backdrop-blur-xl rounded-full px-3 py-1.5 border border-white/15">
                    <div className="w-5 h-5 bg-gradient-to-br from-blue-500/30 to-indigo-500/30 rounded-full flex items-center justify-center">
                      <Briefcase className="w-3 h-3 text-blue-300" />
                    </div>
                    <span className="text-gray-200 text-sm font-medium">{job.jobType}</span>
                  </div>
                )}
                {job.level && (
                  <div className="flex items-center gap-2 bg-white/8 backdrop-blur-xl rounded-full px-3 py-1.5 border border-white/15">
                    <div className="w-5 h-5 bg-gradient-to-br from-violet-500/30 to-purple-500/30 rounded-full flex items-center justify-center">
                      <Users className="w-3 h-3 text-violet-300" />
                    </div>
                    <span className="text-gray-200 text-sm font-medium">{job.level}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 sm:gap-6 text-sm text-gray-400 bg-white/5 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/10">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{formatDeadline(job.closingDate)}</span>
                </div>
                {job.applicationCount > 0 && (
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    <span>{job.applicationCount} applicants</span>
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl hover:bg-white/[0.07] hover:border-white/15 transition-all duration-300"
            >
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-xl flex items-center justify-center border border-amber-500/30">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-amber-300" />
                </div>
                About the Role
              </h2>
              <MarkdownContent content={job.description} />
            </motion.div>

            {job.requirements && (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl hover:bg-white/[0.07] hover:border-white/15 transition-all duration-300"
              >
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-xl flex items-center justify-center border border-amber-500/30">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-300" />
                  </div>
                  Requirements
                </h2>
                <MarkdownContent content={job.requirements} />
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:sticky lg:top-32"
            >
              <div className="relative bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-xl hover:shadow-2xl hover:bg-white/[0.07] hover:border-white/15 transition-all duration-300 overflow-hidden">
                {/* Background gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-yellow-500/5 pointer-events-none"></div>
                
                <div className="relative z-10">
                  {job.hasApplied ? (
                    <div className="text-center">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-green-500/20 to-emerald-500/20 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 border border-green-500/30 shadow-lg">
                        <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-300" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-green-300 to-emerald-300 bg-clip-text text-transparent mb-3">Already Applied</h3>
                      <p className="text-gray-300 text-sm leading-relaxed bg-white/5 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/10">
                        You've already applied for this position. We'll get back to you soon!
                      </p>
                    </div>
                  ) : (
                    <>
                      <motion.button
                        onClick={handleApply}
                        disabled={startingInterview}
                        whileHover={{ scale: startingInterview ? 1 : 1.02 }}
                        whileTap={{ scale: startingInterview ? 1 : 0.98 }}
                        className="w-full py-3 sm:py-4 px-4 sm:px-6 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black font-semibold rounded-xl sm:rounded-2xl hover:from-amber-400 hover:via-yellow-400 hover:to-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-500/30 focus:ring-offset-2 focus:ring-offset-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mb-4 sm:mb-6 relative overflow-hidden shadow-xl"
                      >
                        <div className="absolute inset-0 bg-white/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                        <span className="relative z-10 flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-base">
                          {startingInterview ? (
                            <>
                              <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                              Getting Interview Ready...
                            </>
                          ) : (
                            <>
                              <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                              Apply
                            </>
                          )}
                        </span>
                      </motion.button>

                      {!isAuthenticated && (
                        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-3 sm:p-4 border border-white/10 text-center">
                          <p className="text-gray-300 text-xs sm:text-sm flex items-center justify-center gap-2">
                            <User className="w-6 h-6 sm:w-7 sm:h-7" />
                            You'll need to sign in or create an account to apply
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-white/10">
                    <h3 className="text-white font-bold mb-4 sm:mb-6 flex items-center gap-3">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-lg flex items-center justify-center border border-amber-500/30">
                        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-300" />
                      </div>
                      <span className="text-sm sm:text-base">Application Process</span>
                    </h3>
                    <div className="space-y-4">
                      {[
                        { step: 1, title: 'Submit Application', desc: 'Upload your CV and details', icon: FileText, color: 'from-amber-500/20 to-yellow-500/20 border-amber-500/30', iconColor: 'text-amber-300' },
                        { step: 2, title: 'AI Interview', desc: 'Complete our AI-powered assessment', icon: User, color: 'from-blue-500/20 to-indigo-500/20 border-blue-500/30', iconColor: 'text-blue-300' },
                        { step: 3, title: 'Team Interview', desc: 'Meet with the hiring team', icon: Users, color: 'from-purple-500/20 to-violet-500/20 border-purple-500/30', iconColor: 'text-purple-300' },
                        { step: 4, title: 'Decision', desc: 'Receive our decision', icon: CheckCircle, color: 'from-green-500/20 to-emerald-500/20 border-green-500/30', iconColor: 'text-green-300' }
                      ].map(({ step, title, desc, icon: Icon, color, iconColor }) => (
                        <div key={step} className="flex items-start gap-4 group">
                          <div className={`w-12 h-12 bg-gradient-to-br ${color} backdrop-blur-xl rounded-xl flex items-center justify-center flex-shrink-0 border shadow-lg group-hover:scale-105 transition-transform duration-200`}>
                            <span className="text-white text-sm font-bold">{step}</span>
                          </div>
                          <div className="flex-1 pt-1 min-w-0">
                            <div className="flex items-start gap-2 mb-1">
                              <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0 mt-0.5`} />
                              <p className="text-white text-sm font-semibold leading-tight">{title}</p>
                            </div>
                            <p className="text-gray-400 text-xs leading-relaxed break-words">{desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Interview Modal - For logged-in users */}
      {showInterviewModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center p-4 sm:p-6 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            className="relative bg-gray-900/95 backdrop-blur-2xl border border-white/20 rounded-2xl sm:rounded-3xl p-6 sm:p-10 max-w-md w-full shadow-2xl overflow-hidden"
          >
            {/* Background gradient - darker */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 via-gray-900/50 to-black/30 pointer-events-none"></div>

            <div className="relative z-10">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-400/30 shadow-xl">
                  <Mic className="w-10 h-10 text-amber-300" />
                </div>
                <h3 className="text-3xl font-bold bg-gradient-to-r from-white via-amber-100 to-yellow-200 bg-clip-text text-transparent mb-4">Your AI Interview is Ready!</h3>
                <p className="text-gray-300 mb-3 leading-relaxed">
                  Complete your interview to finalize your application for <strong className="text-white">{job?.title}</strong>
                </p>
                <p className="text-gray-400 text-sm bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                  The interview takes about 15-20 minutes and can be done anytime.
                </p>
              </div>

              <div className="space-y-3">
                <motion.button
                  onClick={() => {
                    setStartingInterview(true);
                    // Navigate to interview page - will be implemented
                    window.location.href = `/interview/${jobId}`; // Temporary navigation
                  }}
                  disabled={startingInterview}
                  whileHover={{ scale: startingInterview ? 1 : 1.02 }}
                  whileTap={{ scale: startingInterview ? 1 : 0.98 }}
                  className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black font-bold rounded-2xl hover:from-amber-400 hover:via-yellow-400 hover:to-amber-500 transition-all duration-300 disabled:opacity-50 relative overflow-hidden shadow-xl"
                >
                  <div className="absolute inset-0 bg-white/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  {startingInterview ? (
                    <span className="flex items-center justify-center gap-3 relative z-10">
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin"></div>
                      Starting Interview...
                    </span>
                  ) : (
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      <Mic className="w-5 h-5" />
                      Start AI Interview Now
                    </span>
                  )}
                </motion.button>

                <motion.button
                  onClick={() => navigate('/dashboard')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-3 px-4 bg-white/10 backdrop-blur-xl hover:bg-white/15 text-gray-200 hover:text-white rounded-2xl transition-all duration-300 border border-white/20 hover:border-white/30"
                >
                  Go to Dashboard
                </motion.button>

                <button
                  onClick={() => setShowInterviewModal(false)}
                  className="w-full py-2 px-3 text-gray-500 hover:text-gray-300 text-sm transition-colors duration-300"
                >
                  Close
                </button>
              </div>

              <p className="text-gray-500 text-xs mt-6 text-center bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                You can also access this interview from your dashboard later
              </p>
            </div>
          </motion.div>
        </div>
      )}

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center p-4 sm:p-6 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            className="relative bg-gray-900/95 backdrop-blur-2xl border border-white/20 rounded-2xl sm:rounded-3xl p-6 sm:p-10 max-w-md w-full shadow-2xl overflow-hidden"
          >
            {/* Background gradient - darker */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 via-gray-900/50 to-black/30 pointer-events-none"></div>

            <div className="relative z-10">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-500/20 to-yellow-500/20 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-6 border border-amber-400/30 shadow-xl">
                  <User className="w-10 h-10 text-amber-300" />
                </div>
                <h3 className="text-3xl font-bold bg-gradient-to-r from-white via-amber-100 to-yellow-200 bg-clip-text text-transparent mb-4">Apply for Position</h3>
                <p className="text-gray-300 leading-relaxed">
                  Sign in with your account or create a new one to apply for this position
                </p>
              </div>

              <div className="space-y-3">
                <Link
                  to="/login"
                  state={{ jobId: jobId, checkStatusOnly: true }}
                  className="block w-full"
                >
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-600 text-black font-bold rounded-2xl hover:from-amber-400 hover:via-yellow-400 hover:to-amber-500 text-center transition-all duration-300 relative overflow-hidden shadow-xl"
                  >
                    <div className="absolute inset-0 bg-white/20 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                    <span className="relative z-10 flex items-center justify-center gap-3">
                      <User className="w-5 h-5" />
                      Sign In
                    </span>
                  </motion.div>
                </Link>

                <Link
                  to="/register"
                  state={{ jobId: jobId, checkStatusOnly: true }}
                  className="block w-full"
                >
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full py-3 px-4 bg-white/10 backdrop-blur-xl text-gray-200 hover:text-white font-semibold rounded-2xl hover:bg-white/15 text-center transition-all duration-300 border border-white/20 hover:border-white/30"
                  >
                    <span className="flex items-center justify-center gap-3">
                      <FileText className="w-5 h-5" />
                      Register
                    </span>
                  </motion.div>
                </Link>

                <button
                  onClick={() => setShowAuthPrompt(false)}
                  className="w-full py-2 px-3 text-gray-500 hover:text-gray-300 text-sm transition-colors duration-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Application Status Modal */}
      <ApplicationStatusModal
        isOpen={showApplicationStatusModal}
        onClose={() => setShowApplicationStatusModal(false)}
        jobTitle={job?.title || ''}
        applicationStatus={applicationDetails.applicationStatus || 'pending'}
        interviewUrl={applicationDetails.interviewUrl}
        appliedDate={applicationDetails.appliedDate}
        interviewStatus={applicationDetails.interviewStatus}
      />
    </div>
  );
};

export default JobDetailPage;